import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ShieldAlert, Copy, CheckCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { readError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function TwoFactorSetup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh, loginWithToken } = useAuth();
  const forced = params.get("forced") === "1";

  const [setup, setSetup] = useState(null); // { secret, otpauth_url, qr_svg }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/2fa/setup");
        if (active) setSetup(data);
      } catch (err) {
        if (active) setError(readError(err, "Could not start 2FA setup."));
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code: code.trim() });
      if (data.access_token) await loginWithToken(data.access_token);
      setRecoveryCodes(data.recovery_codes);
      toast.success("Two-factor authentication enabled");
    } catch (err) {
      setError(readError(err, "Could not enable 2FA."));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    if (!recoveryCodes) return;
    await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setCopied(true);
    toast.success("Recovery codes copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const continueToAdmin = async () => {
    await refresh();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background grain-bg px-4 py-12">
      <div className="mx-auto max-w-3xl animate-fade-up">
        <div className="text-center mb-8">
          <div className="font-serif text-3xl text-foreground leading-tight">
            Tambure <span className="italic text-primary">Žmegač</span>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            Two-factor setup
          </div>
        </div>

        {forced && (
          <div
            data-testid="twofa-forced-banner"
            className="mb-6 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3"
          >
            <ShieldAlert className="h-5 w-5 text-primary mt-0.5" strokeWidth={1.5} />
            <div className="text-sm text-foreground/85">
              <strong className="font-medium">Recovery in progress.</strong> You must re-enable
              two-factor authentication before you can use the admin again.
            </div>
          </div>
        )}

        {recoveryCodes ? (
          <Card className="bg-card border-border/60 shadow-ambient">
            <CardContent className="p-8 sm:p-10 space-y-6">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <AlertTriangle className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-foreground">Save your recovery codes</h2>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    Store these somewhere safe. Each code is single-use and they
                    <strong className="text-foreground"> will not be shown again</strong>.
                  </p>
                </div>
              </div>

              <div
                data-testid="twofa-recovery-codes"
                className="rounded-lg border-2 border-primary/30 bg-primary/5 p-6"
              >
                <div className="grid grid-cols-2 gap-x-8 gap-y-3 font-mono text-sm">
                  {recoveryCodes.map((c, i) => (
                    <div
                      key={c}
                      data-testid={`recovery-code-${i}`}
                      className="flex items-baseline gap-3 text-foreground"
                    >
                      <span className="text-xs text-muted-foreground tabular-nums w-4">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="tracking-[0.12em]">{c}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="twofa-copy-codes-btn"
                  onClick={copyCodes}
                  className="h-11"
                >
                  {copied ? <CheckCheck className="h-4 w-4" strokeWidth={1.5} /> : <Copy className="h-4 w-4" strokeWidth={1.5} />}
                  {copied ? "Copied" : "Copy all"}
                </Button>
                <Button
                  type="button"
                  data-testid="twofa-continue-btn"
                  onClick={continueToAdmin}
                  className="h-11 flex-1 shadow-interactive"
                >
                  I&apos;ve saved my codes — continue
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-card border-border/60 shadow-ambient">
            <CardContent className="p-8 sm:p-10">
              <div className="grid lg:grid-cols-2 gap-10">
                {/* Left — QR */}
                <div>
                  <h2 className="font-serif text-2xl text-foreground">Scan with your authenticator</h2>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    Use Google Authenticator, 1Password, Authy, or any TOTP-compatible app.
                  </p>

                  {setup ? (
                    <>
                      <div
                        data-testid="twofa-qr"
                        className="mt-6 rounded-lg border border-border bg-white p-4 inline-block shadow-ambient"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: setup.qr_svg }}
                      />
                      <div className="mt-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                          Manual entry
                        </div>
                        <div
                          data-testid="twofa-secret"
                          className="font-mono text-sm text-foreground bg-muted/40 rounded-md px-3 py-2 break-all select-all"
                        >
                          {setup.secret}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating QR…
                    </div>
                  )}
                </div>

                {/* Right — verify */}
                <form onSubmit={verify} className="space-y-5" data-testid="twofa-form">
                  <div>
                    <h3 className="font-serif text-xl text-foreground">Verify</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Enter the 6-digit code from your authenticator to enable 2FA.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="code">Authentication code</Label>
                    <Input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      disabled={busy || !setup}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="123 456"
                      className="h-12 bg-card text-center tracking-[0.4em] font-mono text-lg"
                      data-testid="twofa-code-input"
                    />
                  </div>
                  {error && (
                    <div data-testid="twofa-error" className="text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
                      {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={busy || !setup || code.length !== 6}
                    data-testid="twofa-verify-btn"
                    className="w-full h-11 shadow-interactive"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {busy ? "Verifying…" : "Enable 2FA"}
                  </Button>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Signed in as <span className="font-medium text-foreground">{user?.email}</span>
                  </p>
                </form>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
